import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { getClient, IClient, updateClient } from "../../../redux/modules/clients";
import { deleteVisit } from "../../../redux/modules/visits";
import { IVisit } from "../../../redux/modules/visits";
import ClientForm from "../../ClientForm";
import { withRouter } from "../../../hooks/withRouter";
import history from "../../../history";
import { IOperation } from "../../../redux/modules/operations";
import { RootState } from "../../../redux";
import { formatDate } from "../../../utils";
import axiosService from "../../../axios-service";
import "./client-page.css";

interface ClientPageProps {
  clients?: IClient[];
  visits?: IVisit[];
  operations?: IOperation[];
  deleteVisit?: (visitId: number, client: IClient) => void;
  router?: { params?: { id?: string }; location?: { search?: string } };
  getClient?: (clientId: number) => void;
  updateClient?: (client: IClient) => void;
}

type ClientPageState = {
  activeTab: string;
  totals: { servicesTotal: number; productsTotal: number } | null;
  loadingTotals: boolean;
};

class ClientPage extends React.Component<ClientPageProps, ClientPageState> {
  constructor(props: ClientPageProps) {
    super(props);
    const search = (props.router?.location?.search && props.router?.location?.search) || "";
    if (search.indexOf("tab=visits") > -1) {
      this.state = { activeTab: "visits", totals: null, loadingTotals: false };
    } else if (search.indexOf("tab=products") > -1) {
      this.state = { activeTab: "products", totals: null, loadingTotals: false };
    } else {
      this.state = { activeTab: "profile", totals: null, loadingTotals: false };
    }
  }

  getId() {
    const id = this.props?.router?.params?.id;
    if (!id) {
      return 0;
    }
    return +id;
  }

  componentDidMount() {
    const id = this.getId();
    this.props.getClient && this.props.getClient(+id);
  }

  changeActiveTab = (tab: string) => {
    if (tab === this.state.activeTab) {
      return;
    }
    history.push({ search: `?tab=${tab}` });
    this.setState({ activeTab: tab });
  };

  renderClientData() {
    const pageId = this.props?.router?.params?.id;
    if (!this.props.clients || !pageId) {
      return;
    }
    const client = this.props.clients.find(({ id }) => id === +pageId);
    if (!client) {
      return (
        <div className="client-page__empty">
          <p className="client-page__empty-text">Клієнта з id: {pageId} не знайдено</p>
        </div>
      );
    }
    return (
      <div className="client-page__form-container">
        <ClientForm client={client} saveHandler={client.archived ? undefined : this.props.updateClient} readOnly={client.archived} />
      </div>
    );
  }

  getClient() {
    const routerId = this.getId();

    if (!this.props.clients) {
      return null;
    }

    const client = this.props.clients.find(({ id }) => id === +routerId);
    if (!client) {
      return null;
    }
    return client;
  }

  fetchTotals = async () => {
    const id = this.getId();
    if (!id) return;
    this.setState({ loadingTotals: true });
    try {
      const response = await axiosService.get(`/api/v2/clients/${id}/total`);
      this.setState({ totals: response.data });
    } finally {
      this.setState({ loadingTotals: false });
    }
  };

  handleAddVisit = () => {
    const routerId = this.props?.router?.params?.id;
    if (!this.props.clients || !routerId) {
      return;
    }
    const client = this.props.clients.find(({ id }) => id === +routerId);
    if (!client) {
      return;
    }
    history.push(`/new-visit?clientId=${client.id}&clientName=${encodeURIComponent(client.name)}`);
  };

  render() {
    const client = this.getClient();
    
    return (
      <div className="client-page">
        {client && (
          <div className="client-page__header">
            <div className="client-page__header-content">
              <div className="client-page__header-icon">👤</div>
              <div className="client-page__header-info">
                <div className="client-page__title-row">
                  <h1 className="client-page__title">{client.name}</h1>
                  {client.archived && <span className="client-page__archived-badge">Архів</span>}
                </div>
                {client.phone && (
                  <p className="client-page__subtitle">
                    <span className="client-page__label">Телефон:</span> {client.phone}
                  </p>
                )}
                {client.email && (
                  <p className="client-page__subtitle">
                    <span className="client-page__label">Email:</span> {client.email}
                  </p>
                )}
              </div>
              <div className="client-page__totals-area">
                <button
                  className="client-page__btn-totals"
                  onClick={this.fetchTotals}
                  disabled={this.state.loadingTotals}
                >
                  {this.state.loadingTotals ? "..." : "💰 Витрати"}
                </button>
                {this.state.totals && (
                  <div className="client-page__totals">
                    <div className="client-page__total-chip">
                      <span className="client-page__total-label">Послуги</span>
                      <span className="client-page__total-value">{this.state.totals.servicesTotal.toFixed(0)} грн</span>
                    </div>
                    <div className="client-page__total-chip">
                      <span className="client-page__total-label">Косметика</span>
                      <span className="client-page__total-value">{this.state.totals.productsTotal.toFixed(0)} грн</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="client-page__tabs">
          <button
            className={`client-page__tab ${this.state.activeTab === "profile" ? "client-page__tab--active" : ""}`}
            onClick={() => this.changeActiveTab("profile")}
          >
            Профіль
          </button>
          <button
            className={`client-page__tab ${this.state.activeTab === "visits" ? "client-page__tab--active" : ""}`}
            onClick={() => this.changeActiveTab("visits")}
          >
            Візити
          </button>
          <button
            className={`client-page__tab ${this.state.activeTab === "products" ? "client-page__tab--active" : ""}`}
            onClick={() => this.changeActiveTab("products")}
          >
            Косметика
          </button>
        </div>

        <div className="client-page__content">
          {this.state.activeTab === "profile" && (
            <div className="client-page__tab-panel">
              {this.renderClients()}
            </div>
          )}

          {this.state.activeTab === "products" && (
            <div className="client-page__tab-panel">
              <div className="client-page__section">
                <h2 className="client-page__section-title">Продаж косметики</h2>
                {this.renderProducts()}
              </div>
            </div>
          )}

          {this.state.activeTab === "visits" && (
            <div className="client-page__tab-panel">
              <div className="client-page__section">
                <div className="client-page__section-header">
                  <h2 className="client-page__section-title">Візити</h2>
                  {!client?.archived && (
                    <button type="button" className="client-page__btn-add-visit" onClick={this.handleAddVisit}>
                      <span className="client-page__btn-add-icon">➕</span>
                      Створити Візит
                    </button>
                  )}
                </div>
                {this.renderVisits()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderProducts() {
    const client = this.getClient();
    if (!client || !client.operations || !client.operations.length) {
      return (
        <div className="client-page__empty">
          <p className="client-page__empty-text">Немає операцій</p>
        </div>
      );
    }
    const pageId = this.props?.router?.params?.id;
    const operations = client.operations
      .filter((operation) => {
        return pageId && operation.clientId === parseInt(pageId);
      })
      .sort((v1, v2) => {
        return new Date(v2.date).getTime() - new Date(v1.date).getTime();
      });

    return (
      <div className="client-page__list">
        <div className="client-page__list-header">
          <div className="client-page__list-header-item">Назва</div>
          <div className="client-page__list-header-item client-page__list-header-item--center">Дата операції</div>
          <div className="client-page__list-header-item client-page__list-header-item--right">Кількість</div>
        </div>
        {operations.map((operation) => (
          <div key={operation.id} className="client-page__list-item">
            <div className="client-page__list-item-content">
              <div className="client-page__list-item-name">
                <Link to={`/product/${operation.productId}`} className="client-page__list-item-link">
                  {operation?.product?.name || operation.productId}
                </Link>
              </div>
              <div className="client-page__list-item-date">{formatDate(operation?.date)}</div>
              <div className="client-page__list-item-amount">{operation.amount}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  deleteVisit = (visitId: number, client: IClient) => {
    if (window.confirm("Ви впевнені, що хочете видалити цей візит?")) {
      this.props.deleteVisit && this.props.deleteVisit(visitId, client);
    }
  };

  renderVisits() {
    const client = this.getClient();
    if (!client || !client.visits || !client.visits.length) {
      return (
        <div className="client-page__empty">
          <p className="client-page__empty-text">Немає жодного візиту</p>
        </div>
      );
    }
    const pageId = this.props?.router?.params?.id;
    if (!pageId) {
      return (
        <div className="client-page__empty">
          <p className="client-page__empty-text">No page id</p>
        </div>
      );
    }
    const visits = client.visits
      .filter((visit) => {
        return visit.clientId === +pageId;
      })
      .sort((v1, v2) => {
        return new Date(v2.date).getTime() - new Date(v1.date).getTime();
      });

    return (
      <div className="client-page__list">
        <div className="client-page__list-header">
          <div className="client-page__list-header-item">Опис</div>
          <div className="client-page__list-header-item client-page__list-header-item--center">Дата візиту</div>
          <div className="client-page__list-header-item client-page__list-header-item--right"></div>
        </div>
        {visits.map((visit) => (
          <div key={visit.id} className="client-page__list-item">
            <div className="client-page__list-item-content">
              <div className="client-page__list-item-name">
                <Link to={`/visit/${visit.id}`} className="client-page__list-item-link">
                  {visit.title}
                </Link>
              </div>
              <div className="client-page__list-item-date">{formatDate(visit?.date)}</div>
              {!client.archived && (
                <div className="client-page__list-item-actions">
                  <button
                    type="button"
                    className="client-page__btn-delete"
                    onClick={() => this.deleteVisit(visit.id, client)}
                    title="Видалити візит"
                  >
                    <span className="client-page__btn-delete-icon">🗑️</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderClients() {
    if (this.props.clients && Object.keys(this.props.clients).length) {
      return this.renderClientData();
    }
    return (
      <div className="client-page__loading">
        <p className="client-page__loading-text">Завантаження клієнта...</p>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  return { count: state.clientsState.count, clients: Object.values(state.clientsState.clients), visits: state.visits, operations: state.operations };
};

export default withRouter(
  connect(mapStateToProps, {
    getClient,
    deleteVisit,
    updateClient
  })(ClientPage)
);